import { ExclamationCircleIcon } from '@heroicons/react/solid';
import type { StripeCardElementChangeEvent } from '@stripe/stripe-js';
import { useField } from 'formik';
import Error from 'src/shared-components/Form/Error';
import Label from 'src/shared-components/Form/Label';

const errorClass =
  'focus:ring-red-500 focus:border-red-500 border-red-300 text-red-900 placeholder-red-300 focus:outline-none  ';

interface InputFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  required?: boolean;
  className?: string;
  StripeInput?: React.ElementType;
  prefix?: string;
  dataTestId?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement> | StripeCardElementChangeEvent) => void;
  border?: boolean;
  inputRef?: any;
}

const InputField: React.FC<InputFieldProps> = ({
  label,
  required = false,
  className,
  StripeInput,
  prefix,
  dataTestId = '',
  onChange,
  border = true,
  inputRef,
  ...rest
}: InputFieldProps) => {
  const [field, { error, touched }] = useField({
    name: rest.name || '',
    type: rest.type,
  });

  const normalClass = `focus:ring-indigo-500 focus:border-indigo-500 ${border ? 'border-gray-300' : 'border-none'}`;
  const inputClass = `block w-full pr-3 sm:text-sm rounded-md placeholder:text-placeholder appearance-none bg-[#F3F3F3] text-sm text-gray-700 font-normal 
    border-gray-100 py-3 px-4 leading-tight focus:outline-none focus:bg-gray-200 disabled:opacity-50 ${
      error && touched ? errorClass : normalClass
    }`;

  return (
    <div className={`${className}`}>
      {label && <Label text={label} name={rest.name || ''} required={required} />}
      <div className={`${label ? 'mt-1 mb-2' : ''} relative rounded-md`}>
        {StripeInput && (
          <StripeInput onChange={onChange} className={inputClass} options={{ disabled: rest.disabled }} />
        )}
        {!StripeInput && (
          <>
            {prefix && <span className="absolute top-2/4 left-1 -translate-y-1/2">{prefix}</span>}
            <input
              ref={inputRef}
              min="0"
              data-testid={dataTestId}
              aria-label={rest.name}
              className={inputClass}
              {...field}
              {...rest}
              onChange={onChange ? onChange : field.onChange}
            />
          </>
        )}
        {error && touched && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
          </div>
        )}
      </div>
      {error && touched && <Error error={error} />}
    </div>
  );
};

export default InputField;
